import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { Story } from '../mocks/stories';
import { StoryViewer as StoryViewerType, MOCK_FOLLOWERS } from '../mocks/storyViewers';

export const MY_STORY_USER = {
  id: 99,
  username: 'Vous',
  avatar: 'https://public.readdy.ai/ai/img_res/d00912fba40ec40b494b85dc5eab2abf.jpg',
};

const STORY_DURATION_MS = 24 * 60 * 60 * 1000; // 24 heures

export function formatRemaining(ms: number): string {
  if (ms <= 0) return 'Expirée';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h >= 1) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m >= 1) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

export function getExpiryUrgency(ms: number): 'normal' | 'soon' | 'critical' {
  if (ms <= 10 * 60 * 1000) return 'critical';   // < 10 min
  if (ms <= 60 * 60 * 1000) return 'soon';        // < 1 h
  return 'normal';
}

interface StoriesContextValue {
  myStory: Story | null;
  addMyStory: (story: Story) => void;
  clearMyStory: () => void;
  addSlideToMyStory: (slide: { id: number; imageUrl: string; duration: number; caption?: string }) => void;
  pinnedStory: Story | null;
  addPinnedStory: (story: Story) => void;
  clearPinnedStory: () => void;
  viewCount: number;
  viewers: StoryViewerType[];
  remainingMs: number;
  remainingLabel: string;
  urgency: 'normal' | 'soon' | 'critical';
  expiresAt: number | null;
}

const StoriesContext = createContext<StoriesContextValue | null>(null);

export const StoriesProvider = ({ children }: { children: ReactNode }) => {
  const [myStory, setMyStory]           = useState<Story | null>(null);
  const [pinnedStory, setPinnedStory]   = useState<Story | null>(null);
  const [viewCount, setViewCount]       = useState(0);
  const [viewers, setViewers]           = useState<StoryViewerType[]>([]);
  const [remainingMs, setRemainingMs]   = useState(0);
  const [expiresAt, setExpiresAt]       = useState<number | null>(null);

  /* refs */
  const viewTimerRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const expiryTimerRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const followerIdxRef  = useRef(0);
  const expiresAtRef    = useRef<number | null>(null);

  /* ── stop helpers ── */
  const stopViewTimer   = useCallback(() => {
    if (viewTimerRef.current) { clearInterval(viewTimerRef.current); viewTimerRef.current = null; }
  }, []);

  const stopExpiryTimer = useCallback(() => {
    if (expiryTimerRef.current) { clearInterval(expiryTimerRef.current); expiryTimerRef.current = null; }
  }, []);

  /* ── view simulation ── */
  const startViewSimulation = useCallback(() => {
    followerIdxRef.current = 0;
    const pool = [...MOCK_FOLLOWERS];
    const seed = Math.floor(Math.random() * 3) + 2;
    setViewers(pool.slice(0, seed));
    setViewCount(seed);
    followerIdxRef.current = seed;

    viewTimerRef.current = setInterval(() => {
      if (followerIdxRef.current >= pool.length) { stopViewTimer(); return; }
      const next = pool[followerIdxRef.current];
      const minsAgo = Math.floor(followerIdxRef.current * 2.5);
      const label =
        minsAgo === 0 ? 'À l\'instant' :
        minsAgo < 60  ? `Il y a ${minsAgo} min` :
        `Il y a ${Math.floor(minsAgo / 60)}h`;
      setViewers(prev => [{ ...next, viewedAt: label }, ...prev]);
      setViewCount(prev => prev + 1);
      followerIdxRef.current += 1;
    }, Math.floor(Math.random() * 10000) + 8000);
  }, [stopViewTimer]);

  /* ── expiry countdown ── */
  const startExpiryCountdown = useCallback((expires: number) => {
    stopExpiryTimer();
    expiryTimerRef.current = setInterval(() => {
      const left = (expiresAtRef.current ?? 0) - Date.now();
      if (left <= 0) {
        setRemainingMs(0);
        stopExpiryTimer();
      } else {
        setRemainingMs(left);
      }
    }, 1000);
  }, [stopExpiryTimer]);

  /* ── auto-expire when remainingMs hits 0 ── */
  useEffect(() => {
    if (myStory && remainingMs === 0 && expiresAt !== null) {
      setMyStory(null);
      setViewers([]);
      setViewCount(0);
      setExpiresAt(null);
      expiresAtRef.current = null;
      stopViewTimer();
      stopExpiryTimer();
    }
  }, [remainingMs, myStory, expiresAt, stopViewTimer, stopExpiryTimer]);

  /* ── public API ── */
  const addMyStory = useCallback((story: Story) => {
    const expires = Date.now() + STORY_DURATION_MS;
    expiresAtRef.current = expires;
    setMyStory(story);
    setViewers([]);
    setViewCount(0);
    setExpiresAt(expires);
    setRemainingMs(STORY_DURATION_MS);
    startViewSimulation();
    startExpiryCountdown(expires);
  }, [startViewSimulation, startExpiryCountdown]);

  const addSlideToMyStory = useCallback((slide: { id: number; imageUrl: string; duration: number; caption?: string }) => {
    setMyStory(prev => {
      if (!prev) return prev;
      return { ...prev, slides: [...prev.slides, slide] };
    });
  }, []);

  const addPinnedStory = useCallback((story: Story) => {
    setPinnedStory({ ...story, id: Date.now() });
  }, []);

  const clearPinnedStory = useCallback(() => {
    setPinnedStory(null);
  }, []);

  const clearMyStory = useCallback(() => {
    setMyStory(null);
    setViewers([]);
    setViewCount(0);
    setExpiresAt(null);
    expiresAtRef.current = null;
    setRemainingMs(0);
    stopViewTimer();
    stopExpiryTimer();
  }, [stopViewTimer, stopExpiryTimer]);

  /* cleanup */
  useEffect(() => () => { stopViewTimer(); stopExpiryTimer(); }, [stopViewTimer, stopExpiryTimer]);

  const remainingLabel = formatRemaining(remainingMs);
  const urgency        = getExpiryUrgency(remainingMs);

  return (
    <StoriesContext.Provider value={{
      myStory, addMyStory, clearMyStory, addSlideToMyStory,
      pinnedStory, addPinnedStory, clearPinnedStory,
      viewCount, viewers,
      remainingMs, remainingLabel, urgency, expiresAt,
    }}>
      {children}
    </StoriesContext.Provider>
  );
};

export const useStories = (): StoriesContextValue => {
  const ctx = useContext(StoriesContext);
  if (!ctx) throw new Error('useStories must be used within StoriesProvider');
  return ctx;
};
